import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { cn } from "../../utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors rounded-[12px] disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:     "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        // Aliases kept for back-compat with apps that previously forked Button.
        primary:     "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        tertiary:    "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:     "bg-background text-foreground border border-border hover:bg-accent hover:text-accent-foreground",
        secondary:   "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:       "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground",
        success:     "bg-positive text-primary-foreground hover:bg-positive/90",
        link:        "text-primary underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        default: "h-9 px-3.5 text-[13px]",
        sm:      "h-7 px-2.5 text-[12px] rounded-[10px]",
        xs:      "h-6 px-2 text-[11px] rounded-[8px] gap-1",
        lg:      "h-10 px-5 text-[13.5px]",
        icon:    "h-8 w-8 p-0 rounded-[10px]",
        iconSm:  "h-6 w-6 p-0 rounded-[8px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type IconComp = React.ComponentType<{ className?: string }>

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Icon rendered before children. */
  leftIcon?: IconComp
  /** Icon rendered after children. */
  rightIcon?: IconComp
  /** Back-compat alias for `leftIcon`. */
  icon?: IconComp
  /** When true, replaces leftIcon with a spinner and disables pointer events. */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className, variant, size, asChild = false,
      leftIcon, rightIcon, icon, loading,
      children, ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const LeftIcon = leftIcon || icon

    // When using asChild, Radix Slot expects a single child element — so we
    // skip the icon affordances in that mode and let the caller compose freely.
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : LeftIcon ? (
          <LeftIcon className="h-4 w-4" />
        ) : null}
        {children}
        {!loading && rightIcon ? React.createElement(rightIcon, { className: "h-4 w-4" }) : null}
      </Comp>
    )
  }
)
Button.displayName = "Button"

/**
 * Convenience for icon-only buttons. Equivalent to:
 *   <Button variant="ghost" size="icon" aria-label={label}><Icon /></Button>
 */
function IconButton({
  icon: Icon, label, className, ...props
}: { icon: IconComp; label: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      aria-label={label}
      className={cn(buttonVariants({ variant: "ghost", size: "icon" }), className)}
      {...props}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}

export { Button, IconButton, buttonVariants }
